import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import * as XLSX from "xlsx";
import APIs from "../../Constants/apis";
import axiosInstance from "../../config/axiosApi/axiosApi";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import Pagination from "../../components/CustomComponents/Pagination/pagination";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "../order/ManageOrderFilters.css";
import "./marketplaceOrderEarningsReport.css";

const PAGE_KEY = "marketplaceSettlementReport";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !isNaN(date.getTime()) ? date : null;
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Paid", label: "Paid" },
  { value: "Cancelled", label: "Cancelled" },
];

const formatMoney = (n, symbol = "") =>
  `${symbol}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d) => {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format("DD MMM YYYY") : String(d);
};

const statusBadgeClass = (status) => {
  const map = {
    Pending: "bg-warning text-dark",
    Approved: "bg-info text-dark",
    Paid: "bg-success",
    Cancelled: "bg-secondary",
  };
  return map[status] || "bg-secondary";
};

const lineAmount = (line) => Number(line?.amount ?? line?.Amount ?? 0);
const lineType = (line) => (line?.entryType || line?.EntryType || "").toString();

/** Build a clear payout breakdown from ledger lines (includes Penalty). */
const getSettlementBreakdown = (detail) => {
  const lines = detail?.lines || [];
  const sumTypes = (...types) =>
    lines
      .filter((l) => types.includes(lineType(l)))
      .reduce((sum, l) => sum + lineAmount(l), 0);

  const gross = sumTypes("SalePayable");
  const commission = sumTypes("Commission");
  const promo = sumTypes("PlatformPromo");
  const wastage = sumTypes("Wastage", "WastageRefund");
  const penalty = sumTypes("Penalty");
  const adjustments = sumTypes("Adjustment");
  const costReimbursement = sumTypes("CostReimbursement");
  const net =
    lines.length > 0
      ? lines.reduce((sum, l) => sum + lineAmount(l), 0)
      : Number(detail?.netPayable ?? detail?.NetPayable ?? 0);

  return {
    gross,
    commission,
    promo,
    wastage,
    penalty,
    adjustments,
    costReimbursement,
    net,
  };
};

const formatExportDate = (d) => {
  if (!d) return "";
  const m = moment(d);
  return m.isValid() ? m.format("YYYY-MM-DD") : String(d);
};

const getOrderPenalty = (order, lines = []) => {
  const fromOrder = Number(order?.penalty ?? order?.Penalty ?? 0);
  if (fromOrder) return fromOrder;
  const invoice = order?.recvorder_InvoiceNo || order?.Recvorder_InvoiceNo || "";
  if (!invoice) return 0;
  return lines
    .filter(
      (l) =>
        (l.recvorder_InvoiceNo || l.Recvorder_InvoiceNo) === invoice &&
        lineType(l) === "Penalty"
    )
    .reduce((sum, l) => sum + lineAmount(l), 0);
};

/** Build one consolidated order object per invoice (from orders, or from ledger lines). */
const getConsolidatedOrders = (detail) => {
  const lines = detail?.lines || [];
  const orders = detail?.orders || [];
  if (orders.length > 0) return orders;

  const byInvoice = new Map();
  lines.forEach((l) => {
    const invoice = l.recvorder_InvoiceNo || l.Recvorder_InvoiceNo || "(no invoice)";
    if (!byInvoice.has(invoice)) {
      byInvoice.set(invoice, {
        recvorder_InvoiceNo: invoice === "(no invoice)" ? "" : invoice,
        salePayable: 0,
        commission: 0,
        platformPromo: 0,
        wastage: 0,
        wastageRefund: 0,
        penalty: 0,
        adjustment: 0,
        costReimbursement: 0,
        net: 0,
        isCatchUp: false,
      });
    }
    const row = byInvoice.get(invoice);
    const amount = lineAmount(l);
    const type = lineType(l);
    if (type === "SalePayable") row.salePayable += amount;
    else if (type === "Commission") row.commission += amount;
    else if (type === "PlatformPromo") row.platformPromo += amount;
    else if (type === "Wastage") row.wastage += amount;
    else if (type === "WastageRefund") row.wastageRefund += amount;
    else if (type === "Penalty") row.penalty += amount;
    else if (type === "Adjustment") row.adjustment += amount;
    else if (type === "CostReimbursement") row.costReimbursement += amount;
    row.net += amount;
    if (l.isCatchUp || l.IsCatchUp) row.isCatchUp = true;
  });
  return Array.from(byInvoice.values());
};

/** One consolidated row per order (food, commission, promo, wastage, penalty, etc.). */
const buildOrderExportRows = (detail) => {
  const lines = detail?.lines || [];
  return getConsolidatedOrders(detail).map((o) => [
    o.recvorder_InvoiceNo || o.Recvorder_InvoiceNo || "",
    Number(o.salePayable ?? o.SalePayable ?? 0),
    Number(o.commission ?? o.Commission ?? 0),
    Number(o.platformPromo ?? o.PlatformPromo ?? 0),
    Number(o.wastage ?? o.Wastage ?? 0) + Number(o.wastageRefund ?? o.WastageRefund ?? 0),
    getOrderPenalty(o, lines),
    Number(o.adjustment ?? o.Adjustment ?? 0),
    Number(o.costReimbursement ?? o.CostReimbursement ?? 0),
    Number(o.net ?? o.Net ?? 0),
    o.isCatchUp || o.IsCatchUp ? "Yes" : "No",
  ]);
};

/**
 * XLSX: Summary sheet + Orders sheet.
 * Orders sheet = exactly one row per invoice (not separate Penalty/Commission/SalePayable lines).
 */
const downloadSettlementDetailXlsx = (detail) => {
  const breakdown = getSettlementBreakdown(detail);
  const settlementId = detail?.settlement_ID ?? detail?.Settlement_ID ?? "";
  const orderRows = buildOrderExportRows(detail);

  const summarySheet = [
    ["Settlement Summary"],
    [],
    ["Settlement ID", settlementId],
    ["Status", detail?.status || detail?.Status || ""],
    ["Period Start", formatExportDate(detail?.periodStart || detail?.PeriodStart)],
    ["Period End", formatExportDate(detail?.periodEnd || detail?.PeriodEnd)],
    ["Payment Reference", detail?.paymentReference || detail?.PaymentReference || ""],
    ["Paid At", formatExportDate(detail?.paidAt || detail?.PaidAt)],
    ["Order Count", orderRows.length],
    [],
    ["Payout Breakdown"],
    ["Gross (food)", Number(breakdown.gross || 0)],
    ["Commission", Number(breakdown.commission || 0)],
    ["Promo", Number(breakdown.promo || 0)],
    ["Wastage", Number(breakdown.wastage || 0)],
    ["Penalty", Number(breakdown.penalty || 0)],
    ["Other Adjustments", Number(breakdown.adjustments || 0)],
    ["Cost Reimbursement", Number(breakdown.costReimbursement || 0)],
    ["Net Payable", Number(breakdown.net || 0)],
  ];

  const ordersSheet = [
    [
      "Invoice",
      "Food",
      "Commission",
      "Promo",
      "Wastage",
      "Penalty",
      "Adjustments",
      "CostReimbursement",
      "Net",
      "CatchUp",
    ],
    ...orderRows,
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summarySheet), "Summary");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ordersSheet), "Orders");
  XLSX.writeFile(wb, `settlement-${settlementId}-orders.xlsx`);
};

const emptySummary = {
  settlementCount: 0,
  pendingCount: 0,
  approvedCount: 0,
  paidCount: 0,
  cancelledCount: 0,
  grossSalesTotal: 0,
  commissionTotal: 0,
  adjustmentsTotal: 0,
  netPayableTotal: 0,
  netPaidTotal: 0,
  netOutstandingTotal: 0,
  orderCountTotal: 0,
};

const MarketplaceSettlementReport = () => {
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const preferences = useSelector((state) => state.itemGridPreferences[PAGE_KEY] || {});
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const symbol = currencySymbols[getCompCurrency()] || "";

  const defaultDateRange = useMemo(
    () => [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
    []
  );

  const dateRange = (() => {
    const stored = preferences.dateRange;
    if (!stored || !Array.isArray(stored)) return defaultDateRange;
    const start = toValidDate(stored[0]);
    const end = toValidDate(stored[1]);
    if (!start || !end) return defaultDateRange;
    return [start, end];
  })();
  const [rawStartDate, rawEndDate] = dateRange;

  const resolveStatus = (stored) => {
    if (!stored) return STATUS_OPTIONS[0];
    const match = STATUS_OPTIONS.find((o) => o.value === (stored.value ?? stored));
    return match || STATUS_OPTIONS[0];
  };

  const [loading, setLoading] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [statusFilter, setStatusFilterState] = useState(() => resolveStatus(preferences.statusFilter));

  const setStatusFilter = (value) => {
    setStatusFilterState(value);
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "statusFilter", value }));
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, dateRange[1]];
    const nextRange = [
      newStartDate || moment().startOf("month").toDate(),
      newEndDate || moment().endOf("month").toDate(),
    ];
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: nextRange }));
  };

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSizeState] = useState(
    preferences.pageSize || { value: "20", label: "20" }
  );
  const setPageSize = (value) => {
    setPageSizeState(value);
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "pageSize", value }));
  };
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!preferences.dateRange) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
    }
  }, [dispatch, preferences.dateRange, defaultDateRange]);

  const periodFrom = rawStartDate ? moment(rawStartDate).format("YYYY-MM-DD") : null;
  const periodTo = rawEndDate ? moment(rawEndDate).format("YYYY-MM-DD") : null;

  const fetchData = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const params = {
        CompID,
        PageNumber: pageNumber,
        PageSize: pageSize.value,
      };
      if (statusFilter?.value) params.Status = statusFilter.value;
      if (periodFrom) params.PeriodFrom = periodFrom;
      if (periodTo) params.PeriodTo = periodTo;

      const summaryParams = {
        CompID,
        ...(statusFilter?.value ? { Status: statusFilter.value } : {}),
        ...(periodFrom ? { PeriodFrom: periodFrom } : {}),
        ...(periodTo ? { PeriodTo: periodTo } : {}),
      };

      const [listRes, summaryRes] = await Promise.all([
        axiosInstance.get(APIs.BO_GET_MARKETPLACE_SETTLEMENTS, { params }),
        axiosInstance.get(APIs.BO_GET_MARKETPLACE_SETTLEMENT_REPORT_SUMMARY, {
          params: summaryParams,
        }),
      ]);

      if (listRes.data.status) {
        setSettlements(Array.isArray(listRes.data.data) ? listRes.data.data : []);
        setTotalRecords(listRes.data.totalRecords || 0);
        setTotalPages(listRes.data.totalPages || 0);
      } else {
        showErrorToast(listRes.data.message || "Failed to load settlements");
      }

      if (summaryRes.data.status && summaryRes.data.data) {
        const s = summaryRes.data.data;
        setSummary({
          settlementCount: s.settlementCount ?? s.SettlementCount ?? 0,
          pendingCount: s.pendingCount ?? s.PendingCount ?? 0,
          approvedCount: s.approvedCount ?? s.ApprovedCount ?? 0,
          paidCount: s.paidCount ?? s.PaidCount ?? 0,
          cancelledCount: s.cancelledCount ?? s.CancelledCount ?? 0,
          grossSalesTotal: s.grossSalesTotal ?? s.GrossSalesTotal ?? 0,
          commissionTotal: s.commissionTotal ?? s.CommissionTotal ?? 0,
          adjustmentsTotal: s.adjustmentsTotal ?? s.AdjustmentsTotal ?? 0,
          netPayableTotal: s.netPayableTotal ?? s.NetPayableTotal ?? 0,
          netPaidTotal: s.netPaidTotal ?? s.NetPaidTotal ?? 0,
          netOutstandingTotal: s.netOutstandingTotal ?? s.NetOutstandingTotal ?? 0,
          orderCountTotal: s.orderCountTotal ?? s.OrderCountTotal ?? 0,
        });
      } else {
        setSummary(emptySummary);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load settlement report");
    } finally {
      setLoading(false);
    }
  }, [CompID, pageNumber, pageSize.value, statusFilter?.value, periodFrom, periodTo]);

  useEffect(() => {
    const handler = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(handler);
  }, [fetchData]);

  useEffect(() => {
    setPageNumber(1);
  }, [statusFilter, periodFrom, periodTo, pageSize.value]);

  const openDetail = async (row) => {
    const id = row.settlement_ID || row.Settlement_ID;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.BO_GET_MARKETPLACE_SETTLEMENT_BY_ID, {
        params: { CompID, Settlement_ID: id },
      });
      if (response.data.status) {
        setDetail(response.data.data);
        setDetailOpen(true);
      } else {
        showErrorToast(response.data.message || "Failed to load settlement detail");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load settlement detail");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!settlements.length) {
      showErrorToast("No rows to export");
      return;
    }
    const headers = [
      "SettlementID",
      "PeriodStart",
      "PeriodEnd",
      "Orders",
      "GrossSales",
      "Commission",
      "Adjustments",
      "NetPayable",
      "Status",
      "PaidAt",
      "PaymentReference",
    ];
    const lines = settlements.map((r) =>
      [
        r.settlement_ID,
        formatDate(r.periodStart),
        formatDate(r.periodEnd),
        r.orderCount ?? 0,
        Number(r.grossSales || 0).toFixed(2),
        Number(r.commissionTotal || 0).toFixed(2),
        Number(r.adjustmentsTotal || 0).toFixed(2),
        Number(r.netPayable || 0).toFixed(2),
        r.status || "",
        r.paidAt ? formatDate(r.paidAt) : "",
        r.paymentReference || "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `marketplace-settlements-${periodFrom || "all"}-${periodTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDetailOrders = () => {
    if (!detail) {
      showErrorToast("No settlement detail to export");
      return;
    }
    const hasOrders = (detail.orders || []).length > 0;
    const hasLines = (detail.lines || []).length > 0;
    if (!hasOrders && !hasLines) {
      showErrorToast("No order or ledger rows to export");
      return;
    }
    downloadSettlementDetailXlsx(detail);
  };

  const detailBreakdown = useMemo(
    () => (detail ? getSettlementBreakdown(detail) : null),
    [detail]
  );

  const selectStatus = (value) => {
    const next = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
    setStatusFilter(next);
  };

  const absTotal = Math.abs(summary.netPaidTotal) + Math.abs(summary.netOutstandingTotal) || 1;
  const paidPct = Math.min(100, Math.max(0, (Math.abs(summary.netPaidTotal) / absTotal) * 100));
  const outstandingPct = Math.min(100, Math.max(0, (Math.abs(summary.netOutstandingTotal) / absTotal) * 100));

  const moneyCell = (value) => {
    const n = Number(value || 0);
    return (
      <td className={`moe-num ${n === 0 ? "is-zero" : ""}`}>
        {formatMoney(n, symbol)}
      </td>
    );
  };

  const statusChips = [
    { value: "Pending", label: "Pending", count: summary.pendingCount, color: "#f0ad4e" },
    { value: "Approved", label: "Approved", count: summary.approvedCount, color: "#0dcaf0" },
    { value: "Paid", label: "Paid", count: summary.paidCount, color: "#198754" },
    { value: "Cancelled", label: "Cancelled", count: summary.cancelledCount, color: "#6c757d" },
  ];

  return (
    <div className="moe-page">
      {loading && <Preloader />}

      <div className="d-flex align-items-start mb-3 flex-wrap gap-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">Reports</li>
            <li className="breadcrumb-item active">Marketplace Settlements</li>
          </ol>
          <h1 className="page-header mb-1">Marketplace Settlement Report</h1>
          <p className="text-muted mb-0 moe-subtitle">
            Weekly platform payouts to your restaurant. Live order amounts by date are on{" "}
            <NavLink
              to="/report/marketplaceOrderEarningsReport"
              className="moe-earnings-link"
            >
              Marketplace Order Earnings
            </NavLink>
            .
          </p>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="moe-export-btn"
            onClick={exportCsv}
            style={{ backgroundColor: themeColor }}
          >
            <i className="fa fa-download" />
            Export CSV
          </button>
        </div>
      </div>

      <div
        className="card border-0 mb-3 filter-card"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-xl-5 col-lg-6 col-md-6">
              <DateRangePicker
                startDate={rawStartDate}
                endDate={rawEndDate}
                onChange={handleDateRangeChange}
                placeholder="Select Date Range"
                className="filter-dropdown"
              />
            </div>
            <div className="col-xl-4 col-lg-5 col-md-6">
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
                styles={dropdownStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="moe-kpi-row mb-3">
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-file-invoice" />
            </span>
            Settlements
          </div>
          <div className="moe-kpi-value">{summary.settlementCount}</div>
        </div>
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-receipt" />
            </span>
            Orders
          </div>
          <div className="moe-kpi-value">{summary.orderCountTotal}</div>
        </div>
        <div className="moe-kpi-card">
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-chart-line" />
            </span>
            Gross sales
          </div>
          <div className="moe-kpi-value">{formatMoney(summary.grossSalesTotal, symbol)}</div>
        </div>
        <div className="moe-kpi-card moe-kpi-card--accent" style={{ backgroundColor: themeColor }}>
          <div className="moe-kpi-label">
            <span className="moe-kpi-icon">
              <i className="fa fa-wallet" />
            </span>
            Net payable
          </div>
          <div className="moe-kpi-value">{formatMoney(summary.netPayableTotal, symbol)}</div>
        </div>
      </div>

      <div className="moe-split-card mb-3">
        <div className="moe-split-head">
          <div className="moe-split-label">Paid vs outstanding</div>
          <div className="moe-split-amounts">
            <span>Paid {formatMoney(summary.netPaidTotal, symbol)}</span>
            <span>Outstanding {formatMoney(summary.netOutstandingTotal, symbol)}</span>
          </div>
        </div>
        <div className="moe-split-bar mb-3">
          <span style={{ width: `${paidPct}%`, background: "#198754" }} />
          <span style={{ width: `${outstandingPct}%`, background: "#f0ad4e" }} />
        </div>
        <div className="moe-status-chips">
          {statusChips.map((chip) => {
            const active = statusFilter?.value === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                className={`moe-status-chip ${active ? "is-active" : ""}`}
                style={active ? { backgroundColor: chip.color } : undefined}
                onClick={() => selectStatus(active ? "" : chip.value)}
              >
                {chip.label} · {chip.count}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card border-0 moe-table-card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <strong>Weekly settlements</strong>
            <span className="text-muted small ms-2">
              {totalRecords} {totalRecords === 1 ? "record" : "records"}
            </span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Period</th>
                  <th className="moe-num">Orders</th>
                  <th className="moe-num">Gross</th>
                  <th className="moe-num">Commission</th>
                  <th className="moe-num">Adjustments</th>
                  <th className="moe-num">Net</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {settlements.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="moe-empty">
                        <i className="fa fa-inbox" />
                        No settlements for this period.
                      </div>
                    </td>
                  </tr>
                ) : (
                  settlements.map((row) => {
                    const id = row.settlement_ID || row.Settlement_ID;
                    const status = row.status || row.Status || "";
                    const net = Number(row.netPayable ?? row.NetPayable ?? 0);
                    return (
                      <tr key={id}>
                        <td>#{id}</td>
                        <td>
                          {formatDate(row.periodStart)} – {formatDate(row.periodEnd)}
                        </td>
                        <td className="moe-num">{row.orderCount ?? 0}</td>
                        {moneyCell(row.grossSales ?? row.GrossSales)}
                        {moneyCell(row.commissionTotal ?? row.CommissionTotal)}
                        {moneyCell(row.adjustmentsTotal ?? row.AdjustmentsTotal)}
                        <td className={`moe-num ${net === 0 ? "is-zero" : ""}`}>
                          <strong>{formatMoney(net, symbol)}</strong>
                        </td>
                        <td>
                          <span className={`badge moe-payout-badge ${statusBadgeClass(status)}`}>
                            {status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="moe-view-btn"
                            style={{ backgroundColor: themeColor }}
                            onClick={() => openDetail(row)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {(totalPages > 0 || totalRecords > 0) && (
          <div className="px-3 pb-3">
            <Pagination
              totalRecords={totalRecords}
              pageSize={pageSize}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              themeColor={themeColor}
            />
          </div>
        )}
      </div>

      <CustomModal
        title={detail ? `Settlement #${detail.settlement_ID}` : "Settlement"}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        headerActions={
          detail ? (
            <button
              type="button"
              className="moe-modal-download-btn"
              onClick={exportDetailOrders}
              title="Download settlement XLSX"
              aria-label="Download settlement XLSX"
              style={{
                borderColor: themeColor,
                color: isDarkMode ? "#fff" : themeColor,
              }}
            >
              <i className="fa fa-download" />
            </button>
          ) : null
        }
      >
        {detail && (
          <div className="moe-settlement-detail">
            <div className="moe-detail-grid">
              <div className="moe-detail-card">
                <div className="moe-split-label mb-2">Period</div>
                <div className="moe-detail-period">
                  {formatDate(detail.periodStart)} – {formatDate(detail.periodEnd)}
                </div>
                <span className={`badge moe-payout-badge ${statusBadgeClass(detail.status)}`}>
                  {detail.status}
                </span>
                {detail.paymentReference && (
                  <div className="moe-detail-meta">Ref: {detail.paymentReference}</div>
                )}
                {detail.paidAt && (
                  <div className="moe-detail-meta text-muted">Paid: {formatDate(detail.paidAt)}</div>
                )}
              </div>
              <div className="moe-detail-card moe-detail-card--totals">
                <div className="moe-split-label mb-2">Payout breakdown</div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Gross (food)</span>
                  <span>{formatMoney(detailBreakdown?.gross, symbol)}</span>
                </div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Commission</span>
                  <span>{formatMoney(detailBreakdown?.commission, symbol)}</span>
                </div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Promo</span>
                  <span>{formatMoney(detailBreakdown?.promo, symbol)}</span>
                </div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Wastage</span>
                  <span>{formatMoney(detailBreakdown?.wastage, symbol)}</span>
                </div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Penalty</span>
                  <span className={Number(detailBreakdown?.penalty || 0) < 0 ? "text-danger" : ""}>
                    {formatMoney(detailBreakdown?.penalty, symbol)}
                  </span>
                </div>
                <div className="moe-detail-kv">
                  <span className="text-muted">Other adjustments</span>
                  <span>
                    {formatMoney(
                      (detailBreakdown?.adjustments || 0) +
                        (detailBreakdown?.costReimbursement || 0),
                      symbol
                    )}
                  </span>
                </div>
                <div className="moe-detail-kv moe-detail-kv--net">
                  <strong>Net payable</strong>
                  <strong>{formatMoney(detailBreakdown?.net, symbol)}</strong>
                </div>
              </div>
            </div>

            <div className="moe-detail-section">
              <div className="moe-detail-section-head">
                <h6 className="mb-0">Orders</h6>
                <span className="text-muted small">
                  {(detail.orders || []).length}{" "}
                  {(detail.orders || []).length === 1 ? "order" : "orders"}
                  {" · one row per invoice"}
                </span>
              </div>
              <div className="table-responsive moe-detail-table-wrap">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th className="moe-num">Food</th>
                      <th className="moe-num">Commission</th>
                      <th className="moe-num">Promo</th>
                      <th className="moe-num">Wastage</th>
                      <th className="moe-num">Penalty</th>
                      <th className="moe-num">Adj.</th>
                      <th className="moe-num">Cost reimb.</th>
                      <th className="moe-num">Net</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.orders || []).length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-muted text-center py-3">
                          No orders
                        </td>
                      </tr>
                    ) : (
                      detail.orders.map((order) => {
                        const penalty = getOrderPenalty(order, detail.lines || []);
                        return (
                          <tr key={order.recvorder_InvoiceNo || "none"}>
                            <td>
                              <span className="moe-invoice" title={order.recvorder_InvoiceNo || ""}>
                                {order.recvorder_InvoiceNo || "—"}
                              </span>
                            </td>
                            <td className="moe-num">{formatMoney(order.salePayable, symbol)}</td>
                            <td className="moe-num">{formatMoney(order.commission, symbol)}</td>
                            <td className="moe-num">{formatMoney(order.platformPromo, symbol)}</td>
                            <td className="moe-num">
                              {formatMoney((order.wastage || 0) + (order.wastageRefund || 0), symbol)}
                            </td>
                            <td className="moe-num">
                              <span className={penalty < 0 ? "text-danger" : ""}>
                                {formatMoney(penalty, symbol)}
                              </span>
                            </td>
                            <td className="moe-num">{formatMoney(order.adjustment, symbol)}</td>
                            <td className="moe-num">
                              {formatMoney(order.costReimbursement, symbol)}
                            </td>
                            <td className="moe-num">
                              <strong>{formatMoney(order.net, symbol)}</strong>
                            </td>
                            <td>
                              {order.isCatchUp ? (
                                <span className="badge bg-warning text-dark">Prior period</span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default MarketplaceSettlementReport;
